// Source-pipeline parser — the plan-time fold that turns a source op's
// trailing constraint forms into an absorbed `native` blob + a list of
// residual `PlanNode`s.
//
// `parseSourcePipeline` is the synchronous, pure planner the `query` /
// `datapoints` special forms call after decode. It runs as a plan-time
// fold of uniform stages: partition the (already-literalised) predicate
// into the provider's `native` filter + a residual predicate, gate the
// non-predicate constraints (sort / limit / columns) through the
// ordering-legality planner, and assemble whatever was not absorbed into
// ordered residual ops via the universal generic stage.
//
// Cross-dataset set refs and nested dataset-valued sub-queries in the predicate
// are resolved + eagerly collapsed to literals by the whole-AST pass
// (`inlineSetRefs` in `set-refs.ts`) run BEFORE this planner, so the planner
// never sees a dataset-valued node and stays synchronous (no `EvalContext`).
//
// CRITICAL ordering: this runs STRUCTURALLY over the trailing forms —
// they are op-call-shaped AST read as DATA, never dispatched as ops — and
// MUST run BEFORE the per-row lift-kwarg sweep so trailing `:k v` pairs
// are not stolen. Source ops declare no lift kwargs.

import type { AstInput } from '../normalize.ts';
import type { PlanNode } from '../plan.ts';
import { projectSchema, type Schema } from '../schema/types.ts';
import { decodePipeline } from './decode.ts';
import { residualColumnsNode, residualFilterNode, residualLimitNode, residualSortNode } from './generic-stage.ts';
import { type AbsorbCapabilities, type ConstraintKind, planAbsorption } from './legality.ts';
import { type PartitionHooks, partitionPredicate } from './partition.ts';
import type { DecodedParams } from './types.ts';

/**
 * The result of parsing a source pipeline: the provider's absorbed
 * `native` filter blob, the declared schema after absorption +
 * projection, and the residual ops that replay client-side at
 * materialize time, in canonical order.
 *
 * @typeParam NativeType — the provider's native filter shape.
 */
export type ParsedPipeline<NativeType = unknown> = {
  /** The provider's translated native filter for the pushed predicate (absent when nothing pushed). */
  readonly native?: NativeType;
  /** Residual constraint ops to replay client-side, in canonical order. */
  readonly residualOps: readonly PlanNode[];
  /** The declared output schema after absorbed projection (the residual ops' final `schemaAfter`). */
  readonly schema: Schema;
};

/**
 * The provider-supplied inputs `parseSourcePipeline` needs: the predicate
 * partition closures and which non-predicate constraints the provider's
 * backend can apply natively.
 *
 * `absorbableConstraints` lists the constraint kinds the provider CAN
 * push (e.g. a SQL/REST backend lists `['sort', 'limit', 'columns']`;
 * CDF lists `[]` — it absorbs only the filter). The legality planner
 * still gates each against the ordering rule, so listing a constraint
 * here is necessary but not sufficient for absorption.
 *
 * @typeParam NativeType — the provider's native filter shape.
 */
export type PipelineHooks<NativeType = unknown> = PartitionHooks<NativeType> & {
  /** Non-predicate constraint kinds the provider's backend can apply natively. */
  readonly absorbableConstraints: readonly Exclude<ConstraintKind, 'filter'>[];
};

/**
 * One residual-node builder: given the schema entering this constraint,
 * return its replay `PlanNode`, or `undefined` when the constraint is not
 * actually present (so the legality planner and the decoded params can
 * disagree harmlessly). Each builder owns its own `schemaAfter`.
 */
type ResidualNodeBuilder = (schema: Schema) => PlanNode | undefined;

/**
 * Build the ordered residual `PlanNode`s for the constraints the legality
 * planner did NOT absorb. A per-kind builder map keeps the dispatch flat;
 * the fold walks the canonical constraint order (so the residual chain
 * replays filter → sort → columns → limit) and threads each node's
 * `schemaAfter` forward. Only `columns` changes the schema (projection);
 * the others preserve it, so threading the previous schema is correct.
 */
const buildResidualOps = (
  residualKinds: readonly ConstraintKind[],
  residualFilter: AstInput | undefined,
  params: DecodedParams,
  inputSchema: Schema,
): readonly PlanNode[] => {
  const builders: Record<ConstraintKind, ResidualNodeBuilder> = {
    filter: (schema) => (residualFilter === undefined ? undefined : residualFilterNode(residualFilter, schema)),
    sort: (schema) => (params.sort === undefined ? undefined : residualSortNode(params.sort, schema)),
    columns: (schema) =>
      params.columns === undefined
        ? undefined
        : residualColumnsNode(columnsVector(params.columns), params.columns, schema),
    limit: (schema) => (params.limit === undefined ? undefined : residualLimitNode(params.limit, schema)),
  };

  return residualKinds.reduce<{ ops: PlanNode[]; schema: Schema }>(
    (acc, kind) => {
      const node = builders[kind](acc.schema);
      return node === undefined ? acc : { ops: [...acc.ops, node], schema: node.schemaAfter };
    },
    { ops: [], schema: inputSchema },
  ).ops;
};

/** Re-encode a decoded column-name list as the `[:a :b]` keyword vector a `(columns …)` replay node expects. */
const columnsVector = (columns: readonly string[]): AstInput => columns.map((name) => ({ $kw: name }));

/**
 * Parse a source op's decoded constraint pipeline into the provider's
 * absorbed `native` + the residual ops, gated by the ordering-legality
 * rule. `inputSchema` is the provider's declared schema for the dataset
 * (before any `columns` projection).
 *
 * The fold:
 *  1. partition the predicate into the provider's `native` + a residual
 *     predicate via the shared `partitionPredicate`;
 *  2. gate sort / limit / columns through `planAbsorption`, keyed on
 *     whether the filter pushed with zero residual;
 *  3. assemble the non-absorbed constraints into ordered residual ops.
 *
 * `params.rawFilter` is expected to be already fully literalised — any
 * cross-dataset set ref or dataset-valued sub-expression is collapsed to a
 * literal value/vector by the whole-AST pass (`inlineSetRefs` in
 * `set-refs.ts`) run BEFORE this pure planner, so no dataset-valued node ever
 * reaches the partition. Keeping this planner
 * synchronous lets the legality/partition unit tests drive it without an
 * `EvalContext`.
 *
 * @typeParam NativeType — the provider's native filter shape.
 */
export const parseSourcePipeline = <NativeType>(
  params: DecodedParams,
  inputSchema: Schema,
  hooks: PipelineHooks<NativeType>,
): ParsedPipeline<NativeType> => {
  const resolvedFilter = params.rawFilter;
  const { native, residual } = partitionPredicate<NativeType>(resolvedFilter, inputSchema, hooks);
  const filterFullyPushed = resolvedFilter !== undefined && residual === undefined;

  const can = (kind: Exclude<ConstraintKind, 'filter'>): boolean => hooks.absorbableConstraints.includes(kind);
  const caps: AbsorbCapabilities = {
    filter: { present: resolvedFilter !== undefined, providerCapable: true },
    filterFullyPushed,
    sort: { present: params.sort !== undefined, providerCapable: can('sort') },
    columns: { present: params.columns !== undefined, providerCapable: can('columns') },
    limit: { present: params.limit !== undefined, providerCapable: can('limit') },
  };
  const plan = planAbsorption(caps);

  const residualOps = buildResidualOps(plan.residual, residual, params, inputSchema);
  // The declared output schema reflects the `columns` projection whether
  // the provider absorbed it (projected natively on the wire) or it stays
  // residual (the residual `columns` node projects client-side). Either
  // way the declared schema is the input projected to the requested
  // names — the SSOT projection both paths converge on, so declared ==
  // fetched (the size-strict `schemaRefines` post-condition holds).
  return {
    ...(native === undefined ? {} : { native }),
    residualOps,
    schema: projectSchema(inputSchema, params.columns),
  };
};

/**
 * Decode the trailing pipeline forms of a source op AND parse them into
 * `native` + residual ops in one call — the convenience the source ops
 * use. Equivalent to `decodePipeline` followed by `parseSourcePipeline`,
 * kept together so a caller does not have to thread `DecodedParams`
 * through itself.
 *
 * @typeParam NativeType — the provider's native filter shape.
 */
export const decodeAndParsePipeline = <NativeType>(
  forms: readonly AstInput[],
  inputSchema: Schema,
  hooks: PipelineHooks<NativeType>,
): ParsedPipeline<NativeType> => parseSourcePipeline(decodePipeline(forms), inputSchema, hooks);
